import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast"; // 1. Import toast
import Sidebar from "../../components/sidebar/Sidebar";
import SectionHeader from "../../components/sectionHeader/SectionHeader";
import MainSection from "../../components/mainLayout/Main";
import { editNotification, getOneNotification } from "../../redux/actions/authActions";

function EditNotification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { notification, loading } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    targets: "",
    document: null,
  });

  useEffect(() => {
    dispatch(getOneNotification(id));
  }, [dispatch, id]);

  useEffect(() => {
    const data = notification?.notification || notification;
    if (data && data._id === id) {
      setFormData({
        title: data.title || "",
        description: data.description || "",
        targets: data.targets || "",
        document: data.document || null,
      });
    }
  }, [notification, id]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("title", formData.title);
    data.append("description", formData.description);
    data.append("targets", formData.targets);

    try {
      // 2. Wrap the dispatch in a promise check
      await dispatch(editNotification(id, data));
      
      // 3. Trigger Success Toast
      toast.success("Notification updated successfully!");

      // 4. Delay navigation slightly so user sees the toast
      setTimeout(() => {
        navigate("/notifications");
      }, 1500);
      
    } catch (error) {
      toast.error("Failed to update notification.");
    }
  };

  if (loading && !formData.title) {
    return (
      <div className="flex">
        <Sidebar />
        <MainSection>
          <p className="p-10 text-gray-500">Loading notification data...</p>
        </MainSection>
      </div>
    );
  }

  return (
    <div className="flex min-screen bg-gray-50">
      {/* 5. Add Toaster component anywhere in the tree */}
      <Toaster position="top-right" reverseOrder={false} />
      
      <Sidebar />
      <MainSection>
        <div className="p-8 w-full"> 
          <SectionHeader title="Edit Notification" />

          <div className="mt-8 bg-white p-8 rounded-xl shadow-sm w-full border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="title" className="block mb-2 text-sm font-semibold text-gray-700">
                  Notification Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={onChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block mb-2 text-sm font-semibold text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={onChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  rows={5}
                  required
                />
              </div>

              <div>
                <label htmlFor="targets" className="block mb-2 text-sm font-semibold text-gray-700">
                  Audience
                </label>
                <select
                  id="targets"
                  value={formData.targets}
                  onChange={onChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white outline-none"
                  required
                >
                  <option value="" disabled>Select audience</option>
                  <option value="Team">Team</option>
                  <option value="Clients">Clients</option>
                  <option value="Team and Clients">Team and Clients</option>
                </select>
              </div>

              <div className="pt-4 flex gap-4">
                <button 
                  type="submit"
                  disabled={loading} // Prevent double clicks
                  className={`flex-1 ${loading ? 'bg-gray-400' : 'bg-[#045B9B] hover:bg-[#034a7d]'} text-white font-medium py-3 rounded-lg transition-colors shadow-md`}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                <button 
                  type="button"
                  onClick={() => navigate("/notifications")}
                  className="px-6 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </MainSection>
    </div>
  );
}

export default EditNotification;